import { useState } from "react"
import {
	Stack,
	Text,
	Button,
	TextInput,
	Group,
	Title,
	Flex,
	Card,
	Anchor
} from "@mantine/core"
import { useForm } from "@mantine/form"
import OTPInput from "@components/ui/OTPInput/OTPInput"
import { showErrorNotification } from "@components/ui/Notification/Notification"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { stylizeText } from "@utils/stylizeText"
import { CountryCodePicker } from "@components/ui"
import { useIsMobile } from "@utils/device.utils"
import { eventTrackingItems } from "@utils/eventTracking"

interface GenericVerificationFlowProps {
	title: string
	description: string
	inputLabel: string
	inputPlaceholder: string
	inputType?: "email" | "phone"
	inputDisabled?: boolean
	initialValue?: string
	validateInput: (value: string) => string | null
	onSend: (input: string) => Promise<boolean>
	onVerify: (otp: string, input: string) => Promise<boolean>
	resendFunction: (input: string) => void
	goBack: () => void
	isLoading: boolean
}

export const GenericVerificationFlow = ({
	title,
	description,
	inputLabel,
	inputPlaceholder,
	inputType = "email",
	inputDisabled = false,
	initialValue = "",
	isLoading = false,
	validateInput,
	onSend,
	onVerify,
	resendFunction,
	goBack
}: GenericVerificationFlowProps) => {
	const [verificationStep, setVerificationStep] = useState(false)
	const [inputValue, setInputValue] = useState(initialValue || "")
	const [otp, setOtp] = useState("")
	const [telephoneCountryCode, setTelephoneCountryCode] = useState<string>("1")
	const [localPhone, setLocalPhone] = useState<string>("")
	const [lastSentTime, setLastSentTime] = useState<number | null>(null)
	const isMobile = useIsMobile()
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)

	const isPhone = inputType === "phone"
	const isEmailDisabled = inputDisabled && inputType === "email"

	const isInCooldown = () => {
		if (!lastSentTime) return false
		return Date.now() - lastSentTime < 300000
	}

	const form = useForm({
		initialValues: { input: initialValue || "" },
		validate: isEmailDisabled ? {} : { input: validateInput }
	})

	const MAX_PHONE_INPUT_LENGTH = 20
	const sanitizeDigits = (value: string) => value.replace(/\D/g, "")
	const sanitizeLocalPhone = (value: string) =>
		sanitizeDigits(value).slice(0, MAX_PHONE_INPUT_LENGTH)
	const getPhoneText = (countryCode: string, local: string) =>
		`+${countryCode}${sanitizeDigits(local)}`
	const getFinalInput = (formInput: string) => {
		if (isPhone) return getPhoneText(telephoneCountryCode, localPhone)
		if (isEmailDisabled) return initialValue || ""
		return formInput
	}

	const onUpdateEvent = (label?: string, type?: string) => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: label ?? "",
			navLinkText: type ?? ""
		})
	}

	const handleSubmit = async (finalInput: string) => {
		try {
			const responseOk = await onSend(finalInput)
			if (responseOk) {
				setInputValue(finalInput)
				setVerificationStep(true)
				setLastSentTime(Date.now())
				onUpdateEvent(
					dictionary?.sendVerificationCodeButtonLabel,
					"Update Email Address Click"
				)
			} else {
				showErrorNotification("Failed to send OTP")
			}
		} catch (error) {
			const errorMessage =
				error && typeof error === "object" && "message" in error
					? (error as Error).message
					: "Failed to send OTP"
			showErrorNotification(errorMessage)
		}
	}

	const handleChange = async (otp: string) => {
		setOtp(otp)
	}

	const handleVerify = async () => {
		const response = await onVerify(otp, inputValue)
		if (!response) {
			setOtp("")
		}
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: "Continue Click",
			navLinkText: dictionary?.continueButton
		})
	}

	const handleResend = () => {
		if (isInCooldown()) return
		resendFunction(inputValue)
		setLastSentTime(Date.now())
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: "Resend Click",
			navLinkText: dictionary?.doNotReceiveAnCodeMessage
		})
	}

	const handleCancel = () => {
		onUpdateEvent(dictionary?.cancelButtonLabel, "Cancel click")
	}
	return (
		<Card
			bg="var(--background-color-card)"
			radius="md"
			shadow="sm"
			p={isMobile ? 20 : 40}
			maw={920}
		>
			<Stack gap="xs">
				<Flex justify="space-between" align="center">
					<Title fz={24} fw={600}>
						{title}
					</Title>
					<Anchor
						underline="always"
						fw={600}
						component="button"
						onClick={() => {
							goBack()
							handleCancel()
						}}
					>
						{dictionary?.cancelButtonLabel}
					</Anchor>
				</Flex>
				<Text size="md">{description}</Text>

				{verificationStep ? (
					<Stack align="center" gap="xl" mt="xl">
						<Group align="flex-start" gap="xl" justify="space-between">
							<Stack gap="sm" flex={1}>
								<Text fw={600} size="md">
									{inputLabel}
								</Text>
								<Text size="lg">{inputValue}</Text>
								<Stack gap="sm" mt="md">
									<Text size="sm">
										{dictionary?.oneTimeVerificationAlertMessage.replace(
											"{new email address}",
											inputValue
										)}
									</Text>
								</Stack>
							</Stack>
							<Stack gap="md" flex={1} align="flex-start">
								<Text fw={600} size="md">
									{dictionary?.insertCodeTitle}
								</Text>
								<OTPInput onChange={handleChange} disabled={isLoading} />
								{stylizeText(dictionary?.doNotReceiveAnCodeMessage || "", {
									onAnchorClick: handleResend,
									fz: 16
								})}
							</Stack>
						</Group>
						<Button
							variant="dark-blue"
							size="lg"
							onClick={handleVerify}
							disabled={otp.length !== 6}
							fullWidth={isMobile}
							my={10}
						>
							{dictionary?.continueButton}
						</Button>
					</Stack>
				) : (
					<form
						onSubmit={form.onSubmit(({ input }) => {
							const final = getFinalInput(input)
							form.setFieldValue("input", final)
							return handleSubmit(final)
						})}
					>
						<Stack gap="xl" align="flex-start" mt="xl">
							{isPhone ? (
								<>
									<Stack gap={4}>
										<Text fz={18} fw={500}>
											{dictionary?.mobilePhoneNumberLabel}
										</Text>
										<Text
											fz={12}
											fw={400}
											lh={1.5}
											c={"var(--mantine-color-royal-41)"}
										>
											{dictionary?.mobilePhoneDescription}
										</Text>
									</Stack>
									<Group
										w={{ base: "100%", sm: 400 }}
										maw="100%"
										align="flex-end"
										gap="sm"
									>
										<CountryCodePicker
											value={telephoneCountryCode}
											onChange={(val) => {
												setTelephoneCountryCode(val)
												form.setFieldValue(
													"input",
													getPhoneText(val, localPhone)
												)
											}}
											showLabel={true}
										/>
										<TextInput
											flex={1}
											inputMode="numeric"
											pattern="[0-9]*"
											maxLength={MAX_PHONE_INPUT_LENGTH}
											value={localPhone}
											onChange={(e) => {
												const sanitized = sanitizeLocalPhone(
													e.currentTarget.value
												)
												setLocalPhone(sanitized)
												form.setFieldValue(
													"input",
													getPhoneText(telephoneCountryCode, sanitized)
												)
											}}
											error={form.errors.input}
											w={{ base: "100%", sm: "auto" }}
										/>
									</Group>
								</>
							) : isEmailDisabled ? (
								<>
									<Text fz={18} fw={500}>
										{inputLabel}
									</Text>
									<Text
										size="lg"
										style={{
											wordBreak: "break-word",
											overflowWrap: "break-word"
										}}
									>
										{initialValue}
									</Text>
								</>
							) : (
								<TextInput
									type={inputType}
									placeholder={inputPlaceholder}
									label={inputLabel}
									size="lg"
									w={{ base: "100%", sm: 400 }}
									maw="100%"
									radius="md"
									{...form.getInputProps("input")}
									error={form.errors.input}
								/>
							)}
							<Button
								type="submit"
								variant="dark-blue"
								size="lg"
								loading={isLoading}
								w={{ base: "100%", sm: "auto" }}
								maw={{ base: "100%", sm: "none" }}
								styles={{
									label: {
										whiteSpace: "normal",
										wordBreak: "break-word",
										overflowWrap: "break-word"
									}
								}}
							>
								{dictionary?.sendVerificationCodeButtonLabel}
							</Button>
						</Stack>
					</form>
				)}
			</Stack>
		</Card>
	)
}
